import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Mail, ArrowLeft } from 'lucide-react'
import { authService } from '../services/auth'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorModal from '../components/ErrorModal'

interface ForgotPasswordForm {
  email: string
}

export default function ForgotPasswordPage() {
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [showErrorModal, setShowErrorModal] = useState(false)
  
  const { register, handleSubmit, formState: { errors }, getValues } = useForm<ForgotPasswordForm>()

  const onSubmit = async (data: ForgotPasswordForm) => {
    if (loading) return
    
    setLoading(true)
    setError('')

    try {
      const response = await authService.requestPasswordReset(data.email)
      if (response.success) {
        setSent(true)
      } else {
        setError(response.error || '发送重置邮件失败')
        setShowErrorModal(true)
      }
    } catch (err) {
      setError('发送重置邮件失败，请重试')
      setShowErrorModal(true)
    } finally {
      setLoading(false)
    }
  }

  if (sent) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <Link to="/" className="text-2xl font-bold text-gray-900">
              Sku Studio
            </Link>
          </div>
          <div className="mt-8 bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100">
                <Mail className="h-6 w-6 text-green-600" />
              </div>
              <h2 className="mt-6 text-2xl font-bold text-gray-900">
                邮件已发送
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                我们已向 <strong>{getValues('email')}</strong> 发送了密码重置邮件。
              </p>
              <p className="mt-4 text-sm text-gray-600">
                请检查您的邮箱，点击邮件中的链接来重置您的密码。如果没有收到邮件，请检查垃圾邮件文件夹。
              </p>
              <div className="mt-6 space-y-4">
                <button
                  onClick={() => setSent(false)}
                  className="w-full btn-secondary py-2"
                >
                  重新发送邮件
                </button>
                <Link
                  to="/login"
                  className="w-full btn-primary py-2 text-center block"
                >
                  返回登录
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <Link to="/" className="text-2xl font-bold text-gray-900">
            Sku Studio
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-bold text-gray-900">
          忘记密码
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          输入您的邮箱地址，我们将向您发送重置密码的链接
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                邮箱地址
              </label>
              <div className="mt-1">
                <input
                  {...register('email', {
                    required: '请输入邮箱地址',
                    pattern: {
                      value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                      message: '请输入有效的邮箱地址'
                    }
                  })}
                  type="email"
                  autoComplete="email"
                  className="input"
                  placeholder="your@email.com"
                />
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center gap-2 btn-primary py-3 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <>
                    <Mail className="w-4 h-4" />
                    发送重置邮件
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6">
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-gray-500"
            >
              <ArrowLeft className="w-4 h-4" />
              返回登录
            </Link>
          </div>
        </div>
      </div>

      {/* 错误弹窗 */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="发送失败"
        message={error}
      />
    </div>
  )
}